import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  AcademicCapIcon,
  PlusIcon,
  XMarkIcon,
  ChartBarIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import studiesService, {
  Study,
  CreateStudyDto,
  UpdateStudyDto,
  FilterStudyDto,
} from '../services/studies';
import StudyCard from '../components/StudyCard';
import StudyFormModal from '../components/StudyFormModal';
import SubjectStatisticsModal from '../components/SubjectStatisticsModal';
import LoadingSpinner from '../components/LoadingSpinner';

const Studies: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasCleanedUrlRef = useRef(false);
  const [studies, setStudies] = useState<Study[]>([]);
  const [subjects, setSubjects] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [themes, setThemes] = useState<string[]>([]);
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showStatisticsModal, setShowStatisticsModal] = useState(false);
  const [editingStudy, setEditingStudy] = useState<Study | null>(null);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  // Filter state - initialize with search param from URL if present
  const [filters, setFilters] = useState<FilterStudyDto>(() => {
    const searchParam = searchParams.get('search');
    return {
      search: searchParam || '',
      status: undefined,
      what: undefined,
      priority: undefined,
      category: undefined,
      subject: undefined,
      tags: undefined,
      page: 1,
      limit: 12,
      sortBy: 'start',
      sortOrder: 'desc',
    };
  });

  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 12,
  });

  // Check if it's the first visit
  useEffect(() => {
    const hasVisitedStudies = localStorage.getItem('hasVisitedStudies');
    if (!hasVisitedStudies) {
      setShowWelcomeModal(true);
    }
  }, []);

  // Clean up search parameter from URL after it's been read
  useEffect(() => {
    if (!hasCleanedUrlRef.current) {
      const searchParam = searchParams.get('search');
      if (searchParam) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('search');
        setSearchParams(newSearchParams, { replace: true });
        hasCleanedUrlRef.current = true;
      }
    }
  }, [searchParams, setSearchParams]);

  const handleCloseWelcomeModal = () => {
    setShowWelcomeModal(false);
    localStorage.setItem('hasVisitedStudies', 'true');
  };

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load studies first (main data)
      const studiesData = await studiesService.getStudies(filters);
      setStudies(studiesData.studies || []);
      setPagination({
        total: studiesData.total || 0,
        page: studiesData.page || 1,
        limit: studiesData.limit || 12,
      });

      // Extract unique themes, types, and platforms from all studies
      if (studiesData.studies && studiesData.studies.length > 0) {
        const uniqueThemes = Array.from(new Set(studiesData.studies.map((s: Study) => s.theme).filter(Boolean)));
        const uniqueTypes = Array.from(new Set(studiesData.studies.map((s: Study) => s.what).filter(Boolean)));
        const uniquePlatforms = Array.from(new Set(studiesData.studies.map((s: Study) => s.where).filter(Boolean)));
        setThemes(uniqueThemes as string[]);
        setTypes(uniqueTypes as string[]);
        setPlatforms(uniquePlatforms as string[]);
      }

      // Load additional data (non-critical, handle failures gracefully)
      try {
        const subjectsData = await studiesService.getSubjects();
        setSubjects(subjectsData || []);
      } catch (error) {
        console.warn('Failed to load subjects:', error);
        setSubjects([]);
      }

      try {
        const categoriesData = await studiesService.getCategories(filters.subject);
        setCategories(categoriesData || []);
      } catch (error) {
        console.warn('Failed to load categories:', error);
        setCategories([]);
      }
    } catch (error) {
      console.error('Erro ao carregar estudos:', error);
      toast.error('Falha ao carregar estudos');
      // Set empty arrays to prevent further errors
      setStudies([]);
      setSubjects([]);
      setCategories([]);
      setThemes([]);
      setTypes([]);
      setPlatforms([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStudy = async (data: CreateStudyDto) => {
    try {
      await studiesService.createStudy(data);
      toast.success('Estudo criado com sucesso!');
      loadData();
      setShowModal(false);
    } catch (error) {
      console.error('Erro ao criar estudo:', error);
      toast.error('Falha ao criar estudo');
    }
  };

  const handleUpdateStudy = async (id: string, data: UpdateStudyDto) => {
    try {
      await studiesService.updateStudy(id, data);
      toast.success('Estudo atualizado com sucesso!');
      loadData();
      setShowModal(false);
      setEditingStudy(null);
    } catch (error) {
      console.error('Erro ao atualizar estudo:', error);
      toast.error('Falha ao atualizar estudo');
    }
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50/30 to-blue-50 dark:from-gray-900 dark:via-teal-900/10 dark:to-cyan-900/20 transition-all duration-500">
      <div className="max-w-6xl mx-auto px-2 sm:px-4 lg:px-6 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="space-y-2">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 dark:from-teal-400 dark:via-cyan-400 dark:to-blue-400 bg-clip-text text-transparent">
                Estudos
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                Crie com IA e organize seus planos de estudo de forma eficiente e personalizada.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
              <button
                onClick={() => setShowModal(true)}
                className="w-full sm:w-auto inline-flex items-center justify-center px-4 sm:px-6 py-2.5 sm:py-3 border border-transparent text-sm font-semibold rounded-xl shadow-lg text-white bg-gradient-to-r from-teal-500 via-cyan-600 to-blue-600 hover:from-teal-600 hover:via-cyan-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 dark:focus:ring-offset-gray-900 transform hover:scale-105 transition-all duration-200 backdrop-blur-sm shadow-teal-500/25 hover:shadow-teal-600/30"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Novo Estudo
              </button>
              <button
                onClick={() => setShowStatisticsModal(true)}
                className="w-full sm:w-auto inline-flex items-center justify-center px-4 sm:px-5 py-2.5 sm:py-3 border border-teal-500 dark:border-teal-400 text-sm font-semibold rounded-xl shadow-md text-teal-600 dark:text-teal-400 bg-white dark:bg-gray-800 hover:bg-teal-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 dark:focus:ring-offset-gray-900 transform hover:scale-105 transition-all duration-200"
              >
                <ChartBarIcon className="h-5 w-5 mr-2" />
                Estatísticas
              </button>
            </div>
          </div>
        </div>

        {/* Studies List */}
        {loading ? (
          <LoadingSpinner />
        ) : (
          <div className="space-y-4">
            {studies.length === 0 ? (
              <div className="text-center py-12">
                <AcademicCapIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Nenhum estudo encontrado</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Comece criando seu primeiro plano de estudo.
                </p>
                <div className="mt-6">
                  <button
                    onClick={() => setShowModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Criar Primeiro Estudo
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {studies.map((study) => (
                  <div key={study._id} className="flex flex-col bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {study.subject || 'Sem assunto'}
                      </h3>
                      {study.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-4">
                          {study.description}
                        </p>
                      )}
                    </div>
                    <div className="mt-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        study.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                        study.status === 'in_progress' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' :
                        study.status === 'paused' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                        'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                      }`}>
                        {study.status === 'not_started' ? 'Não Iniciado' :
                         study.status === 'in_progress' ? 'Em Andamento' :
                         study.status === 'completed' ? 'Concluído' :
                         'Pausado'}
                      </span>
                    </div>
                    <button
                      onClick={() => navigate(`/studies/${study._id}`)}
                      className="mt-4 w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg text-white bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      <RectangleStackIcon className="h-5 w-5 mr-2" />
                      Módulos
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        {pagination.total > pagination.limit && (
          <div className="mt-8 flex items-center justify-between">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Mostrando {((pagination.page - 1) * pagination.limit) + 1} a {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total} estudos
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Anterior
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page * pagination.limit >= pagination.total}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Próximo
              </button>
            </div>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <StudyFormModal
            study={editingStudy}
            onSubmit={editingStudy ? (data) => handleUpdateStudy(editingStudy._id, data) : handleCreateStudy}
            onClose={() => {
              setShowModal(false);
              setEditingStudy(null);
            }}            subjects={subjects}
            categories={categories}
            themes={themes}
            types={types}
            platforms={platforms}          />
        )}

        {/* Welcome Modal */}
        {showWelcomeModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-gray-900/75 dark:bg-gray-900/90 backdrop-blur-sm transition-opacity"
              onClick={handleCloseWelcomeModal}
              aria-hidden="true"
            />

            {/* Modal */}
            <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-teal-500 via-cyan-600 to-blue-600 px-6 py-5 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0 h-12 w-12 rounded-full bg-white/20 flex items-center justify-center">
                      <AcademicCapIcon className="h-7 w-7 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white">
                      Bem-vindo ao módulo de Estudos!
                    </h3>
                  </div>
                  <button
                    onClick={handleCloseWelcomeModal}
                    className="text-white/90 hover:text-white hover:bg-white/10 rounded-lg p-1.5 transition-all"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="bg-white dark:bg-gray-800 px-6 py-6 overflow-y-auto flex-grow">
                  <p className="text-gray-700 dark:text-gray-300 mb-6 text-lg">
                    Este módulo ajuda você criar estudos completos usando inteligência artificial, organizar seu aprendizado e acompanhar seu progresso. Aqui estão algumas funcionalidades que você pode explorar:
                  </p>

                  <div className="space-y-5">

                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                        <span className="text-2xl">✨</span>
                      </div>
                      <div className="ml-4">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                          Gerar Módulos com IA
                        </h4>
                        <p className="text-gray-600 dark:text-gray-400">
                          Na tela de <strong>Módulos</strong> de cada estudo, deixe a IA dividir o assunto em uma lista de tópicos organizados, do básico ao avançado.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-fuchsia-100 dark:bg-fuchsia-900/30 flex items-center justify-center">
                        <span className="text-2xl">📖</span>
                      </div>
                      <div className="ml-4">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                          Texto de Estudo com IA
                        </h4>
                        <p className="text-gray-600 dark:text-gray-400">
                          Gere um conteúdo completo e didático para cada módulo com um clique. No plano gratuito você pode gerar até <strong>3 módulos com IA</strong>; no Premium é ilimitado.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                        <span className="text-2xl">📊</span>
                      </div>
                      <div className="ml-4">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                          Acompanhar Progresso
                        </h4>
                        <p className="text-gray-600 dark:text-gray-400">
                          Marque os módulos como concluídos para acompanhar seu avanço.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center">
                        <span className="text-2xl">⏰</span>
                      </div>
                      <div className="ml-4">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                          Agendar Revisões
                        </h4>
                        <p className="text-gray-600 dark:text-gray-400">
                          Configure lembretes automáticos para revisar materiais periodicamente. Receba emails no intervalo que você definir!
                        </p>
                      </div>
                    </div>
                </div>

                <div className="mt-8 p-4 bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 rounded-lg border border-teal-200 dark:border-teal-700">
                  <div className="flex items-start">
                    <span className="text-xl mr-3 flex-shrink-0">💡</span>
                    <p className="text-sm text-teal-800 dark:text-teal-300">
                      <strong>Dica:</strong> Comece clicando no botão <strong>"Novo Estudo"</strong> no canto superior direito. Depois, acesse <strong>"Módulos"</strong> para gerar tópicos e conteúdo com IA — use o ícone de engrenagem para excluir um estudo ou módulo quando precisar.
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 dark:bg-gray-900/50 px-6 py-4 flex-shrink-0 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleCloseWelcomeModal}
                  className="w-full inline-flex justify-center items-center px-6 py-3.5 text-base font-semibold rounded-lg text-white bg-gradient-to-r from-teal-500 via-cyan-600 to-blue-600 hover:from-teal-600 hover:via-cyan-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 dark:focus:ring-offset-gray-800 shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
                >
                  Entendi, vamos começar! 🚀
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Subject Statistics Modal */}
        <SubjectStatisticsModal
          isOpen={showStatisticsModal}
          onClose={() => setShowStatisticsModal(false)}
        />
      </div>
    </div>
  );
};

export default Studies;
